/**
 * MindfulnessProgramDetailScreen - Shows details of a mindfulness program template
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { ProgramTemplateResult } from '../../../Model/Coach/ProgramTemplateResult';
import { MindfulnessProgramTemplatePhase } from '../../../Model/Coach/Mindfulness/Template/MindfulnessProgramTemplatePhase';
import { Theme } from '../../../Theme/Theme';
import { MindfulnessProgramTemplate, MindfulnessProgramTemplateApi } from '../../../Model/Coach/Mindfulness/Template/MindfulnessProgramTemplate';
import { MindfulnessProgram, MindfulnessProgramApi } from '../../../Model/Coach/Mindfulness/MindfulnessProgram';
import { Button } from '../../../Components/Button';
import { getMediaLargeOrMediumOrSmallest } from '../../../Model/Media/Media';
import { getAthleteAllTags } from '../../../Model/Athlete/Athlete';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const PageContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  min-height: calc(100vh - 64px);
  background: ${Theme.Colors.background};
`;

const HeroSection = styled.div<{ $image?: string }>`
  position: relative;
  background: ${(props) =>
    props.$image
      ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${props.$image})`
      : 'linear-gradient(135deg, #8B5CF6, #7C3AED)'};
  background-size: cover;
  background-position: center;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 32px 0;
  color: white;

  @media (max-width: 768px) {
    min-height: 250px;
    padding: 24px 0;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 24px;
  left: 24px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 24px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;

  @media (max-width: 768px) {
    padding-top: 48px;
  }
`;

const BadgesRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const Badge = styled.span<{ $color?: string }>`
  display: inline-block;
  background: ${({ $color }) => $color || 'rgba(255,255,255,0.2)'};
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 12px;
  border-radius: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.1rem;
  margin: 0;
  max-width: 600px;
  line-height: 1.6;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ContentSection = styled.div`
  padding: 32px 0 48px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div``;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionIcon = styled.span`
  font-size: 20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const StatCard = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 12px;
  padding: 16px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #8B5CF6;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${Theme.Colors.charcoal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ResultsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ResultItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const ResultIcon = styled.span`
  font-size: 20px;
  color: #8B5CF6;
`;

const ResultText = styled.span`
  font-size: 0.95rem;
  color: ${Theme.Colors.textColor};
  line-height: 1.5;
`;

const JourneyTimeline = styled.div`
  position: relative;
  padding-left: 24px;

  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: ${Theme.Colors.lighterGrey};
  }
`;

const JourneyPhase = styled.div`
  position: relative;
  padding-bottom: 20px;

  &:last-child {
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #8B5CF6;
    border: 2px solid white;
    box-shadow: 0 0 0 2px #8B5CF6;
  }
`;

const PhaseName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 4px;
`;

const PhaseDetails = styled.div`
  font-size: 0.85rem;
  color: ${Theme.Colors.charcoal};
`;

const CTASection = styled(Section)`
  position: sticky;
  top: 24px;
`;

const CTAButton = styled(Button)`
  width: 100%;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  background: #8B5CF6;
  border-color: #8B5CF6;

  &:hover {
    background: #7C3AED;
    border-color: #7C3AED;
  }
`;

const CTASecondaryButton = styled(Button)`
  width: 100%;
  padding: 12px 24px;
  font-size: 0.9rem;
`;

const ActiveProgramBanner = styled.div`
  background: #8B5CF615;
  border: 1px solid #8B5CF630;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  text-align: center;
`;

const ActiveProgramText = styled.p`
  font-size: 0.9rem;
  color: #8B5CF6;
  margin: 0 0 8px 0;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${Theme.Colors.charcoal};
`;

const NotFoundContainer = styled.div`
  text-align: center;
  padding: 80px 24px;
`;

const NotFoundIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const NotFoundTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const NotFoundText = styled.p`
  font-size: 0.95rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

// Extracted components to avoid hooks-in-loops
const MindfulnessResultItemComponent: React.FC<{ result: ProgramTemplateResult }> = observer(({ result }) => {
  const resultName = useLocalized(result.name);
  return (
    <ResultItem>
      <ResultIcon>✓</ResultIcon>
      <ResultText>{resultName}</ResultText>
    </ResultItem>
  );
});

const MindfulnessPhaseComponent: React.FC<{ phase: MindfulnessProgramTemplatePhase; index: number }> = observer(({ phase, index }) => {
  const { t } = useTrans();
  const phaseName = useLocalized(phase.name);
  return (
    <JourneyPhase>
      <PhaseName>
        {phaseName ||
          t('mindfulnessPrograms.phase.default', {
            defaultValue: 'Phase {{number}}',
            number: index + 1,
          })}
      </PhaseName>
      <PhaseDetails>
        {phase.cycles} {t('mindfulnessPrograms.meta.weeks', { defaultValue: 'weeks' })} •{' '}
        {phase.days.length}x {t('mindfulnessPrograms.perWeek', { defaultValue: 'per week' })}
      </PhaseDetails>
    </JourneyPhase>
  );
});

export type MindfulnessProgramDetailScreenProps = {};

export const MindfulnessProgramDetailScreen: React.FC<MindfulnessProgramDetailScreenProps> = observer(() => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { t } = useTrans();
  const { authentication } = useRootStore();

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<MindfulnessProgramTemplate | null>(null);
  const [activeProgram, setActiveProgram] = useState<MindfulnessProgram | null>(null);
  const [starting, setStarting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEndConfirmModal, setShowEndConfirmModal] = useState(false);
  const [existingPrograms, setExistingPrograms] = useState<MindfulnessProgram[]>([]);

  // Hooks MUST be called before any early returns (Rules of Hooks)
  const title = useLocalized(template?.name);
  const description = useLocalized(template?.description);

  useEffect(() => {
    if (!templateId) return;

    Promise.all([
      MindfulnessProgramTemplateApi.findOne(templateId),
      MindfulnessProgramApi.currentlyActive(),
    ])
      .then(([tmpl, programs]) => {
        setTemplate(tmpl);
        setExistingPrograms(programs);
        const active = programs.find((p) => p.programTemplateId === templateId);
        setActiveProgram(active || null);
      })
      .finally(() => setLoading(false));
  }, [templateId]);

  const handleBack = () => {
    navigate('/mental-health/mindfulness-programs');
  };

  const handleStartProgram = async () => {
    if (!template) return;

    if (existingPrograms.length > 0 && !activeProgram) {
      setShowConfirmModal(true);
      return;
    }

    await startProgram();
  };

  const startProgram = async (endOthers = false) => {
    if (!template) return;

    setStarting(true);
    setShowConfirmModal(false);

    try {
      if (endOthers) {
        await Promise.all(existingPrograms.map((p) => p.end()));
      }

      await template.create({
        programTemplateId: template.id,
        tags: authentication.athlete ? getAthleteAllTags(authentication.athlete) : [],
        schedule: {
          entries: {},
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Zurich',
          notificationIntervals: [],
          timesOfDay: {},
        },
      });

      navigate('/account#programs');
    } catch (error) {
      console.error('Error starting program:', error);
    } finally {
      setStarting(false);
    }
  };

  const handleEndProgram = () => {
    setShowEndConfirmModal(true);
  };

  const confirmEndProgram = async () => {
    if (!activeProgram) return;

    setShowEndConfirmModal(false);
    try {
      await activeProgram.end();
      setActiveProgram(null);
      setExistingPrograms((prev) => prev.filter((p) => p.id !== activeProgram.id));
    } catch (error) {
      console.error('Error ending program:', error);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spinner color="primary" />
          <p style={{ marginTop: 16 }}>{t('common.loading', { defaultValue: 'Loading...' })}</p>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (!template) {
    return (
      <PageContainer>
        <Container>
          <NotFoundContainer>
            <NotFoundIcon>🔍</NotFoundIcon>
            <NotFoundTitle>
              {t('mindfulnessPrograms.notFound.title', { defaultValue: 'Program Not Found' })}
            </NotFoundTitle>
            <NotFoundText>
              {t('mindfulnessPrograms.notFound.description', {
                defaultValue: 'This program could not be found.',
              })}
            </NotFoundText>
            <Button color="primary" onClick={handleBack} style={{ marginTop: 24 }}>
              {t('mindfulnessPrograms.notFound.backButton', { defaultValue: 'Back to Programs' })}
            </Button>
          </NotFoundContainer>
        </Container>
      </PageContainer>
    );
  }

  const image = template.getImage(authentication.athlete ? getAthleteAllTags(authentication.athlete) : []);
  const imageUrl = image ? getMediaLargeOrMediumOrSmallest(image) : undefined;

  const typeLabel = template.type === 'meditation'
    ? t('mindfulnessPrograms.type.meditation', { defaultValue: 'Meditation' })
    : template.type === 'breathing'
    ? t('mindfulnessPrograms.type.breathing', { defaultValue: 'Breathing' })
    : t('mindfulnessPrograms.type.mindfulness', { defaultValue: 'Mindfulness' });

  return (
    <PageContainer>
      <HeroSection $image={imageUrl}>
        <BackButton onClick={handleBack}>
          ← {t('common.back', { defaultValue: 'Back' })}
        </BackButton>
        <HeroContent>
          <BadgesRow>
            <Badge $color="#8B5CF6">{typeLabel}</Badge>
          </BadgesRow>
          <HeroTitle>{title}</HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <ContentGrid>
            <MainContent>
              {description && (
                <Section>
                  <SectionTitle>
                    <SectionIcon>📝</SectionIcon>
                    {t('mindfulnessPrograms.section.about', { defaultValue: 'About This Program' })}
                  </SectionTitle>
                  <p style={{ fontSize: '0.95rem', color: Theme.Colors.textColor, lineHeight: 1.6, margin: 0 }}>
                    {description}
                  </p>
                </Section>
              )}
              <Section>
                <SectionTitle>
                  <SectionIcon>📊</SectionIcon>
                  {t('mindfulnessPrograms.section.overview', { defaultValue: 'Program Overview' })}
                </SectionTitle>
                <StatsGrid>
                  <StatCard>
                    <StatValue>{template.weeksCount}</StatValue>
                    <StatLabel>{t('mindfulnessPrograms.stat.weeks', { defaultValue: 'Weeks' })}</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {template.minDaysPerWeek === template.maxDaysPerWeek
                        ? template.minDaysPerWeek
                        : `${template.minDaysPerWeek}-${template.maxDaysPerWeek}`}
                    </StatValue>
                    <StatLabel>{t('mindfulnessPrograms.stat.daysPerWeek', { defaultValue: 'Days/Week' })}</StatLabel>
                  </StatCard>
                  {template.minDurationInMinutes && (
                    <StatCard>
                      <StatValue>
                        {template.minDurationInMinutes === template.maxDurationInMinutes
                          ? template.minDurationInMinutes
                          : `${template.minDurationInMinutes}-${template.maxDurationInMinutes}`}
                      </StatValue>
                      <StatLabel>{t('mindfulnessPrograms.stat.minutes', { defaultValue: 'Minutes' })}</StatLabel>
                    </StatCard>
                  )}
                  <StatCard>
                    <StatValue>{template.totalUnits}</StatValue>
                    <StatLabel>{t('mindfulnessPrograms.stat.sessions', { defaultValue: 'Sessions' })}</StatLabel>
                  </StatCard>
                </StatsGrid>
              </Section>

              {template.expectedResults.length > 0 && (
                <Section>
                  <SectionTitle>
                    <SectionIcon>🎯</SectionIcon>
                    {t('mindfulnessPrograms.section.results', { defaultValue: 'What You\'ll Achieve' })}
                  </SectionTitle>
                  <ResultsList>
                    {template.expectedResults.map((result, index) => (
                      <MindfulnessResultItemComponent key={index} result={result} />
                    ))}
                  </ResultsList>
                </Section>
              )}

              {template.phases.length > 0 && (
                <Section>
                  <SectionTitle>
                    <SectionIcon>🗓️</SectionIcon>
                    {t('mindfulnessPrograms.section.journey', { defaultValue: 'Your Journey' })}
                  </SectionTitle>
                  <JourneyTimeline>
                    {template.phases.map((phase, index) => (
                      <MindfulnessPhaseComponent key={phase.id} phase={phase} index={index} />
                    ))}
                  </JourneyTimeline>
                </Section>
              )}
            </MainContent>

            <Sidebar>
              <CTASection>
                {activeProgram ? (
                  <>
                    <ActiveProgramBanner>
                      <ActiveProgramText>
                        {t('mindfulnessPrograms.active.message', {
                          defaultValue: 'You are currently doing this program',
                        })}
                      </ActiveProgramText>
                    </ActiveProgramBanner>
                    <CTAButton onClick={() => navigate('/account#programs')}>
                      {t('mindfulnessPrograms.active.viewProgress', { defaultValue: 'View Progress' })}
                    </CTAButton>
                    <CTASecondaryButton color="link" onClick={handleEndProgram}>
                      {t('mindfulnessPrograms.active.endProgram', { defaultValue: 'End Program' })}
                    </CTASecondaryButton>
                  </>
                ) : (
                  <>
                    <CTAButton onClick={handleStartProgram} disabled={starting}>
                      {starting
                        ? t('mindfulnessPrograms.starting', { defaultValue: 'Starting...' })
                        : t('mindfulnessPrograms.startProgram', { defaultValue: 'Start Program' })}
                    </CTAButton>
                    <p style={{ fontSize: '0.85rem', color: Theme.Colors.charcoal, textAlign: 'center', margin: 0 }}>
                      {t('mindfulnessPrograms.startHint', {
                        defaultValue: 'Your program will begin immediately',
                      })}
                    </p>
                  </>
                )}
              </CTASection>
            </Sidebar>
          </ContentGrid>
        </Container>
      </ContentSection>

      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          {t('mindfulnessPrograms.confirm.title', { defaultValue: 'You have other programs running' })}
        </ModalHeader>
        <ModalBody>
          {t('mindfulnessPrograms.confirm.message', {
            defaultValue:
              'Do you want to start this one as well or end the other programs and just start this one?',
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowConfirmModal(false)}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="warning" onClick={() => startProgram(true)}>
            {t('mindfulnessPrograms.confirm.endOthers', { defaultValue: 'End others & start' })}
          </Button>
          <Button color="primary" onClick={() => startProgram(false)} style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}>
            {t('mindfulnessPrograms.confirm.startToo', { defaultValue: 'Start this too' })}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showEndConfirmModal} toggle={() => setShowEndConfirmModal(false)}>
        <ModalHeader toggle={() => setShowEndConfirmModal(false)}>
          {t('mindfulnessPrograms.endConfirm.title', { defaultValue: 'End Program?' })}
        </ModalHeader>
        <ModalBody>
          {t('mindfulnessPrograms.endConfirm.message', {
            defaultValue: 'Are you sure you want to end this program? Your progress will be saved but you will need to start over if you want to continue.',
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEndConfirmModal(false)}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="danger" onClick={confirmEndProgram}>
            {t('mindfulnessPrograms.endConfirm.confirm', { defaultValue: 'End Program' })}
          </Button>
        </ModalFooter>
      </Modal>
    </PageContainer>
  );
});
